/**
 * explodeGesture – how far apart a student's gesture pulls a model's parts.
 *
 * The model tools take an explode amount from 0 (assembled) to 1 (fully apart).
 * Students drive it two ways in a headset, and both land here so the feel is
 * the same whichever they use:
 *
 *   - Two fists pulled apart. The CHANGE in spread since the fists closed is
 *     what counts, not the spread itself: students start the gesture with
 *     their hands wherever they happen to be, and an absolute mapping would
 *     jump the model to some arbitrary amount the moment both fists close.
 *     0.35 m of extra spread — a comfortable pull, well inside arm's reach
 *     when seated — takes a model from assembled to fully apart.
 *   - Grip plus the thumbstick. The stick is a rate, not a position: held
 *     fully over it moves 0.8 of the range per second, so a full explode takes
 *     just over a second — quick, but slow enough to stop where you want.
 *
 * As in twoPointGesture, hitting 0 or 1 re-anchors the fist gesture there, so
 * pulling further and then bringing the hands back responds at once instead
 * of doing nothing until the hands pass the point where the limit was hit.
 *
 * Pure: numbers in, a number out, so it is tested under node:test (see
 * tests/twoPointGesture.test.mts) without hands, controllers or a renderer.
 */

/** Extra spread between the fists, in metres, that equals a full explode. */
export const FULL_EXPLODE_SPREAD_M = 0.35;

/** Explode change per second with the stick held fully over. */
export const STICK_EXPLODE_RATE = 0.8;

/**
 * Stick deflection ignored as rest. Worn thumbsticks rarely read a clean zero,
 * and a thumb resting on the stick while gripping would otherwise creep the
 * model apart over a lesson.
 */
export const STICK_DEADZONE = 0.2;

/**
 * The longest frame the stick step will integrate. The render loop pauses while
 * a headset's system menu is open or the session is hidden; the first frame
 * back then reports a gap of seconds, which with the grip still held would
 * throw the model most of the way apart in one step.
 */
const MAX_STICK_DT_S = 0.1;

/**
 * Mutable per-gesture state. Create it with `beginExplode` when both fists
 * close and drop it when either opens (or when the teacher's explode takes over
 * this asset); `updateExplode` edits it in place.
 */
export interface ExplodeGestureState {
  /** Spread and amount the current change is measured from; moves when 0 or 1 is hit. */
  anchorDistance: number;
  anchorAmount: number;
  /** Latest output, held through bad samples. */
  amount: number;
}

/** Clamp to 0..1; anything non-finite reads as assembled. */
function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Start a fist-spread gesture from the fists' current separation and the model's current amount. */
export function beginExplode(distance: number, amount: number): ExplodeGestureState {
  const start = clamp01(amount);
  return {
    anchorDistance: Number.isFinite(distance) ? distance : NaN,
    anchorAmount: start,
    amount: start,
  };
}

/**
 * The explode amount for the fists' current separation, in metres. A missing
 * reading (a hand dropped out of tracking) holds the amount, and the next good
 * one re-anchors rather than jumping by however far the hands moved unseen.
 */
export function updateExplode(state: ExplodeGestureState, distance: number): number {
  if (!Number.isFinite(distance)) {
    state.anchorDistance = NaN;
    return state.amount;
  }
  if (!Number.isFinite(state.anchorDistance)) {
    state.anchorDistance = distance;
    state.anchorAmount = state.amount;
  }
  const raw = state.anchorAmount + (distance - state.anchorDistance) / FULL_EXPLODE_SPREAD_M;
  const amount = clamp01(raw);
  if (amount !== raw) {
    state.anchorDistance = distance;
    state.anchorAmount = amount;
  }
  state.amount = amount;
  return amount;
}

/**
 * One frame of stick-driven explode.
 *
 * `stickY` is the raw WebXR thumbstick Y axis, where pushing FORWARD reads
 * NEGATIVE. Forward pushes the parts apart and back brings them together —
 * "push it out", the same direction that means go for teleport. Past the dead
 * zone the rate ramps from zero rather than starting at 20% of full speed, so
 * easing the stick off the rest position does not lurch.
 */
export function stickExplodeStep(amount: number, stickY: number, dt: number): number {
  const current = clamp01(amount);
  if (!Number.isFinite(stickY) || !(dt > 0)) return current;
  const deflection = Math.min(Math.abs(stickY), 1);
  if (deflection <= STICK_DEADZONE) return current;
  const drive = (deflection - STICK_DEADZONE) / (1 - STICK_DEADZONE);
  const step = drive * STICK_EXPLODE_RATE * Math.min(dt, MAX_STICK_DT_S);
  return clamp01(stickY < 0 ? current + step : current - step);
}
